import { createContext, useContext, useEffect, useRef, useState } from "react";
import ApiConnector from "../connector/ApiConnector";

const POLL_INTERVAL_MS = 2000;
const KEEP_ALIVE_INTERVAL_MS = 60 * 1000;
const STORAGE_KEY = "activeExportJob";

export const EXPORT_STATUS_INIT = "INIT";
export const EXPORT_STATUS_FILE_EXISTS = "FILE_EXISTS";
export const EXPORT_STATUS_IN_PROGRESS = "IN_PROGRESS";
export const EXPORT_STATUS_DONE = "DONE";
export const EXPORT_STATUS_FAILED = "FAILED";

const defaultState = {
  status: EXPORT_STATUS_INIT,
  jobId: null,
  existingFileName: "",
  presignedUrl: "",
  progress: { percent: null, writtenRows: 0, totalRows: null },
  errorMessage: "",
  navDismissed: false,
};

const ExportContext = createContext(null);

export function ExportProvider({ children }) {
  const [state, setState] = useState(() => {
    // Restore from localStorage on page load (F5 recovery)
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only restore if job was in progress — otherwise start fresh
        if (parsed.jobId && parsed.status === EXPORT_STATUS_IN_PROGRESS) {
          return { ...defaultState, ...parsed };
        }
      }
    } catch (e) {
      // ignore corrupt storage
    }
    return { ...defaultState };
  });

  const pollInterval = useRef(null);
  const keepAliveInterval = useRef(null);
  const jobStartTime = useRef(null);
  const networkErrorCount = useRef(0);
  const MAX_NETWORK_ERRORS = 5;

  // On mount: if we restored an in-progress job from localStorage, resume polling
  useEffect(() => {
    if (state.status === EXPORT_STATUS_IN_PROGRESS && state.jobId) {
      jobStartTime.current = Date.now(); // ETA starts fresh after F5 — no stored start time
      startPolling(state.jobId);
      startKeepAlive();
    }
    return () => {
      clearInterval(pollInterval.current);
      clearInterval(keepAliveInterval.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist active job to localStorage whenever jobId/status changes
  useEffect(() => {
    if (state.jobId && state.status === EXPORT_STATUS_IN_PROGRESS) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ jobId: state.jobId, status: state.status })
      );
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [state.jobId, state.status]);

  const startPolling = (jobId) => {
    clearInterval(pollInterval.current);
    pollInterval.current = setInterval(() => pollStatus(jobId), POLL_INTERVAL_MS);
  };

  const startKeepAlive = () => {
    clearInterval(keepAliveInterval.current);
    keepAliveInterval.current = setInterval(() => {
      document.dispatchEvent(new MouseEvent("mousedown"));
    }, KEEP_ALIVE_INTERVAL_MS);
  };

  const stopAll = () => {
    clearInterval(pollInterval.current);
    clearInterval(keepAliveInterval.current);
    jobStartTime.current = null;
    networkErrorCount.current = 0;
  };

  const pollStatus = async (jobId) => {
    try {
      const job = await ApiConnector.getExportStatus(jobId);

      networkErrorCount.current = 0;

      if (job.status === "IN_PROGRESS" || job.status === "PENDING") {
        setState((prev) => ({
          ...prev,
          progress: {
            percent: job.progressPercent ?? null,
            writtenRows: job.writtenRows ?? 0,
            totalRows: job.totalRows ?? null,
          },
        }));
        return;
      }

      stopAll();

      if (job.status === "DONE") {
        setState((prev) => ({
          ...prev,
          status: EXPORT_STATUS_DONE,
          presignedUrl: job.presignedUrl,
          progress: { ...prev.progress, percent: 100 },
        }));
      } else if (job.status === "FAILED") {
        setState((prev) => ({
          ...prev,
          status: EXPORT_STATUS_FAILED,
          errorMessage: job.errorMessage || "Export failed.",
        }));
      }
    } catch (e) {
      networkErrorCount.current += 1;
      if (networkErrorCount.current < MAX_NETWORK_ERRORS) {
        // Transient network error (e.g. server restart) — keep polling, will recover
        return;
      }
      stopAll();
      setState((prev) => ({
        ...prev,
        status: EXPORT_STATUS_FAILED,
        errorMessage: "Failed to fetch export status: " + e.message,
      }));
    }
  };

  const startJob = (jobId) => {
    jobStartTime.current = Date.now();
    setState({
      ...defaultState,
      status: EXPORT_STATUS_IN_PROGRESS,
      jobId,
    });
    startPolling(jobId);
    startKeepAlive();
  };

  const setFileExists = (fileName) => {
    setState((prev) => ({
      ...prev,
      status: EXPORT_STATUS_FILE_EXISTS,
      existingFileName: fileName,
    }));
  };

  const reset = () => {
    stopAll();
    setState({ ...defaultState });
  };

  /** Called when user visits the Report page — hides the nav-bar indicator without resetting the export state. */
  const markSeen = () => {
    setState((prev) => ({ ...prev, navDismissed: true }));
  };

  const cancelJob = async () => {
    const jobId = state.jobId;
    stopAll();
    setState({ ...defaultState });
    if (jobId) {
      try {
        await ApiConnector.cancelExport(jobId);
      } catch (e) {
        // ignore — UI already reset
      }
    }
  };

  const getEta = () => {
    if (!jobStartTime.current || !state.progress.totalRows || state.progress.writtenRows <= 0)
      return null;
    const elapsedMs = Date.now() - jobStartTime.current;
    const rowsPerMs = state.progress.writtenRows / elapsedMs;
    const remainingRows = state.progress.totalRows - state.progress.writtenRows;
    const remainingMs = remainingRows / rowsPerMs;
    if (!remainingMs || remainingMs <= 0) return null;
    const totalSec = Math.round(remainingMs / 1000);
    if (totalSec < 60) return `~${totalSec}s remaining`;
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    if (min < 60) return `~${min}m ${sec}s remaining`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `~${h}h ${m}m remaining`;
  };

  const isExportActive = state.status === EXPORT_STATUS_IN_PROGRESS;
  const isExportDone = state.status === EXPORT_STATUS_DONE && !state.navDismissed;

  return (
    <ExportContext.Provider
      value={{
        ...state,
        isExportActive,
        isExportDone,
        startJob,
        setFileExists,
        reset,
        cancelJob,
        markSeen,
        getEta,
      }}
    >
      {children}
    </ExportContext.Provider>
  );
}

export function useExport() {
  const ctx = useContext(ExportContext);
  if (!ctx) throw new Error("useExport must be used inside ExportProvider");
  return ctx;
}
